import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
} from 'react-native';
import Svg, { Path, Circle, Rect, Line } from 'react-native-svg';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/config';
import { requiresGuardian } from '../../utils/age';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const FONTS = {
  regular: 'JF-Flat-Regular',
  medium: 'JF-Flat-Medium',
};

const BRAND_RED = '#A9211F';
const BRAND_RED_HOVER = '#8B1A18';
const FOREGROUND = '#414042';
const BORDER = '#E2E8F0';
const MUTED = '#64748B';
const SUCCESS_GREEN = '#22C55E';
const DANGER_RED = '#EF4444';
const DARK_BG = '#1F2937';

/** Convert "HH:MM" 24h to "hh:MM AM/PM" 12h display */
function to12h(time24: string): string {
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr || '0', 10);
  const m = mStr || '00';
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${period}`;
}

/** Convert "HH:MM - HH:MM" range to 12h format */
function formatTimeRange12h(range: string): string {
  const parts = range.split('-').map(s => s.trim());
  if (parts.length === 2) return `${to12h(parts[0])} - ${to12h(parts[1])}`;
  return range;
}
const CREAM_BG = '#FAF8F4';
const CREAM_BORDER = '#E8E4DC';

/* Field type colors for specialization cards */
const FIELD_COLORS: Record<string, string> = {
  artistic: '#A9211F',
  cultural: '#8B5E34',
  sports: '#2563EB',
  digital: '#7C3AED',
  scientific: '#059669',
};

const getFieldColor = (field?: string): string => {
  if (!field) return BRAND_RED;
  return FIELD_COLORS[field] || BRAND_RED;
};

/* Light tinted backgrounds for selected specialization cards */
const FIELD_BG_COLORS: Record<string, string> = {
  artistic: '#FEF2F2',
  cultural: '#FDF8F3',
  sports: '#EFF6FF',
  digital: '#F5F3FF',
  scientific: '#ECFDF5',
};

const getFieldBgColor = (field?: string): string => {
  if (!field) return '#FEF2F2';
  return FIELD_BG_COLORS[field] || '#FEF2F2';
};

/* ------------------------------------------------------------------ */
/*  Inline SVG Icons                                                   */
/* ------------------------------------------------------------------ */

function CheckCircleIcon({ size = 64, color = SUCCESS_GREEN }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth={1.5} />
      <Path d="M9 12l2 2 4-4" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function AlertIcon({ size = 20, color = DANGER_RED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function BuildingIcon({ size = 16, color = MUTED }: { size?: number; color?: string }) {
  /* Lucide building-2: building with side wings and horizontal lines */
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M10 6h4M10 10h4M10 14h4M10 18h4" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function MapPinIcon({ size = 16, color = MUTED }: { size?: number; color?: string }) {
  /* Lucide map-pinned: pin dropping onto a map shape */
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M18 8c0 4.5-6 9-6 9s-6-4.5-6-9a6 6 0 0 1 12 0" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Circle cx="12" cy="8" r="2" stroke={color} strokeWidth={1.5} />
      <Path d="M8.835 14H5a1 1 0 0 0-.9.7l-2 6c-.1.1-.1.2-.1.3 0 .6.4 1 1 1h18c.6 0 1-.4 1-1 0-.1 0-.2-.1-.3l-2-6a1 1 0 0 0-.9-.7h-3.835" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function CalendarIcon({ size = 16, color = MUTED }: { size?: number; color?: string }) {
  /* Lucide calendar-clock: calendar with clock overlay */
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M16 2v4M8 2v4M3 10h5" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M17.5 17.5 16 16.3V14" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <Circle cx="16" cy="16" r="6" stroke={color} strokeWidth={1.5} />
    </Svg>
  );
}

function CheckIcon({ size = 12, color = '#FFFFFF' }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M20 6L9 17l-5-5" stroke={color} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/* ------------------------------------------------------------------ */
/*  FormField Component                                                */
/* ------------------------------------------------------------------ */

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <View style={fieldStyles.container}>
      <Text style={fieldStyles.label}>
        {required && <Text style={{ color: '#D62728' }}>* </Text>}
        {label}
      </Text>
      {children}
    </View>
  );
}

const fieldStyles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: FOREGROUND,
    marginBottom: 6,
    textAlign: 'right',
  },
});

/* ------------------------------------------------------------------ */
/*  Native select style helper (web only)                              */
/* ------------------------------------------------------------------ */

const nativeSelectStyle = (hasValue: boolean): React.CSSProperties => ({
  backgroundColor: '#FFFFFF',
  border: '1px solid #FFFFFF',
  borderRadius: 8,
  padding: '8px 12px',
  minHeight: 36,
  fontFamily: FONTS.regular,
  fontSize: 14,
  color: hasValue ? FOREGROUND : MUTED,
  writingDirection: 'rtl',
  width: '100%',
  boxSizing: 'border-box' as any,
  outline: 'none',
  boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
  WebkitAppearance: 'none' as any,
  MozAppearance: 'none' as any,
  appearance: 'none' as any,
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394A3B8' stroke-width='2' fill='none'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'left 12px center',
  paddingLeft: 32,
  cursor: 'pointer',
});

/* ------------------------------------------------------------------ */
/*  Dropdown Component (for non-web platforms)                         */
/* ------------------------------------------------------------------ */

function Dropdown({
  value,
  label,
  options,
  open,
  onToggle,
  onSelect,
  placeholder,
}: {
  value: string;
  label: string;
  options: { value: string; label: string }[];
  open: boolean;
  onToggle: () => void;
  onSelect: (val: string) => void;
  placeholder?: string;
}) {
  return (
    <View style={{ position: 'relative', zIndex: open ? 100 : 1 }}>
      <TouchableOpacity
        style={[dropdownStyles.trigger, open && dropdownStyles.triggerOpen]}
        onPress={onToggle}
        activeOpacity={0.7}
      >
        <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
          <Path d="M6 9l6 6 6-6" stroke={MUTED} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </Svg>
        <Text style={[dropdownStyles.triggerText, !label && { color: MUTED }]}>
          {label || placeholder || 'اختر...'}
        </Text>
      </TouchableOpacity>
      {open && (
        <View style={dropdownStyles.menu}>
          {options.map((opt) => (
            <TouchableOpacity
              key={opt.value}
              style={[dropdownStyles.menuItem, value === opt.value && dropdownStyles.menuItemSelected]}
              onPress={() => { onSelect(opt.value); onToggle(); }}
            >
              <Text style={[dropdownStyles.menuItemText, value === opt.value && dropdownStyles.menuItemTextSelected]}>
                {opt.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
}

const dropdownStyles = StyleSheet.create({
  trigger: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#FFFFFF',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  triggerOpen: {
    borderColor: '#94A3B8',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  triggerText: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: FOREGROUND,
    textAlign: 'right',
    marginRight: 8,
  },
  menu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderTopWidth: 0,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    maxHeight: 200,
    zIndex: 999,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'visible' as any } : {}),
  },
  menuItem: {
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  menuItemSelected: {
    backgroundColor: '#FEF2F2',
  },
  menuItemText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: FOREGROUND,
    textAlign: 'right',
  },
  menuItemTextSelected: {
    color: BRAND_RED,
    fontFamily: FONTS.medium,
  },
});

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function TalentRegistrationScreen() {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const isDesktop = width >= 1024;

  // Inject CSS to override blue focus outlines with red
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    document.title = 'مبادرة مواهب — تسجيل الطالب';
    const style = document.createElement('style');
    style.textContent = `*:focus { outline-color: ${BRAND_RED} !important; } *:focus-visible { outline-color: ${BRAND_RED} !important; }`;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // ---- URL Params ----
  const [deptId, setDeptId] = useState<string>('');

  // ---- Form state ----
  const [name, setName] = useState('');
  const [eid, setEid] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emirate, setEmirate] = useState('');

  // Format phone: only digits, max 9, display as 5X XXX XXXX
  const handlePhoneChange = (text: string) => {
    const digits = text.replace(/[^0-9]/g, '');
    // If user pastes with leading 0 or 971, strip it
    let cleaned = digits;
    if (cleaned.startsWith('971')) cleaned = cleaned.slice(3);
    if (cleaned.startsWith('0')) cleaned = cleaned.slice(1);
    // Max 9 digits
    cleaned = cleaned.slice(0, 9);
    setPhone(cleaned);
  };

  // Display formatted phone
  const formattedPhone = phone
    ? `${phone.slice(0, 2)} ${phone.slice(2, 5)} ${phone.slice(5)}`.trim()
    : '';
  const [school, setSchool] = useState('');
  const [schoolOptions, setSchoolOptions] = useState<{ value: string; label: string }[]>([]);
  const [gender, setGender] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [nationality, setNationality] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  // Guardian block — required for students under 15, who cannot hold an
  // independent UAE PASS identity. The rule is derived from the birth date,
  // never chosen by the applicant.
  const [guardianName, setGuardianName] = useState('');
  const [guardianEid, setGuardianEid] = useState('');
  const [guardianEmail, setGuardianEmail] = useState('');
  const [guardianPhone, setGuardianPhone] = useState('');
  const [guardianRelation, setGuardianRelation] =
    useState<'father' | 'mother' | 'legal_guardian'>('father');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [selectedSpecs, setSelectedSpecs] = useState<number[]>([]);
  const eidRef = useRef<TextInput>(null);
  const emailRef = useRef<TextInput>(null);
  const phoneRef = useRef<TextInput>(null);

  // Force LTR direction on inputs that display numbers/emails (RTL global CSS override)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    [eidRef, emailRef, phoneRef].forEach((ref) => {
      const node = ref.current as any;
      if (node?.setNativeProps) {
        try { node.setNativeProps({ style: { writingDirection: 'ltr' } }); } catch {}
      }
      // Direct DOM access for web
      const el = node?._nativeTag || node;
      if (el instanceof HTMLElement) {
        el.style.setProperty('direction', 'ltr', 'important');
        el.style.setProperty('text-align', 'left', 'important');
      }
    });
  });

  // ---- Dropdown states ----
  const [showGenderDropdown, setShowGenderDropdown] = useState(false);
  const [showAgeGroupDropdown, setShowAgeGroupDropdown] = useState(false);
  const [showEmirateDropdown, setShowEmirateDropdown] = useState(false);
  const [showNationalityDropdown, setShowNationalityDropdown] = useState(false);
  const [showGuardianRelationDropdown, setShowGuardianRelationDropdown] = useState(false);
  const [nationalitySearch, setNationalitySearch] = useState('');

  // ---- Data ----
  const [specializations, setSpecializations] = useState<{ id: number; name: string; field?: string; description?: string; location?: string; start_date?: string; end_date?: string; activity_schedules?: { dates: string[]; times: string[]; days: string[] }[]; department_id?: number | null }[]>([]);

  // ---- Carousel state ----
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Auto-rotate carousel every 4 seconds
  useEffect(() => {
    if (specializations.length <= 1) return;
    const timer = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % specializations.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [specializations.length]);

  // ---- UI state ----
  const [loading, setLoading] = useState(false);
  const [specsLoading, setSpecsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ---- Pre-filled from SSO/UAE Pass ----
  const [prefilledFromSSO, setPrefilledFromSSO] = useState(false);
  const [ssoFields, setSsoFields] = useState<Set<string>>(new Set());

  // ---- Existing student mode (add specializations) ----
  const [existingStudentMode, setExistingStudentMode] = useState(false);
  const [existingStudentId, setExistingStudentId] = useState<number | null>(null);
  const [existingStudentName, setExistingStudentName] = useState('');
  const [existingSpecIds, setExistingSpecIds] = useState<number[]>([]);

  // ---- Parse URL on mount + pre-fill from SSO if available ----
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const dept = params.get('dept') || '';
      setDeptId(dept);

      // Check for SSO user data saved during no_role redirect
      const savedUser = sessionStorage.getItem('register_user');
      if (savedUser) {
        sessionStorage.removeItem('register_user');
        try {
          const user = JSON.parse(savedUser);
          const uaePass = user.uae_pass_profile || {};

          // Pre-fill from UAE Pass profile and track which fields were set
          const locked = new Set<string>();

          const nameAr = (uaePass.fullname_ar || '').trim();
          const nameEn = (uaePass.fullname_en || '').trim();
          if (nameAr && nameAr !== nameEn) {
            setName(nameAr); locked.add('name');
          } else if (nameEn) {
            setName(nameEn); locked.add('name');
          } else if (user.full_name) {
            setName(user.full_name); locked.add('name');
          }

          if (uaePass.idn) { handleEidChange(uaePass.idn); locked.add('eid'); }
          if (uaePass.email || user.email) { setEmail(uaePass.email || user.email); locked.add('email'); }
          if (uaePass.mobile) {
            let p = uaePass.mobile.replace(/[^0-9]/g, '');
            if (p.startsWith('971')) p = p.slice(3);
            if (p.startsWith('0')) p = p.slice(1);
            setPhone(p.slice(0, 9));
            locked.add('phone');
          }
          if (uaePass.gender) {
            const g = uaePass.gender.toUpperCase();
            if (g === 'MALE') setGender('male');
            else if (g === 'FEMALE') setGender('female');
            locked.add('gender');
          }
          if (uaePass.nationality_ar) {
            // UAE Pass returns adjective form — normalize ى→ي then map to country name
            const natMap: Record<string, string> = {
              'اماراتي': 'الإمارات', 'اماراتية': 'الإمارات',
              'سعودي': 'السعودية', 'سعودية': 'السعودية',
              'كويتي': 'الكويت', 'كويتية': 'الكويت',
              'بحريني': 'البحرين', 'بحرينية': 'البحرين',
              'عماني': 'عمان', 'عمانية': 'عمان',
              'قطري': 'قطر', 'قطرية': 'قطر',
              'اردني': 'الأردن', 'اردنية': 'الأردن',
              'فلسطيني': 'فلسطين', 'فلسطينية': 'فلسطين',
              'لبناني': 'لبنان', 'لبنانية': 'لبنان',
              'سوري': 'سوريا', 'سورية': 'سوريا',
              'عراقي': 'العراق', 'عراقية': 'العراق',
              'يمني': 'اليمن', 'يمنية': 'اليمن',
              'مصري': 'مصر', 'مصرية': 'مصر',
              'ايراني': 'إيران', 'ايرانية': 'إيران',
              'تركي': 'تركيا', 'تركية': 'تركيا',
              'سوداني': 'السودان', 'سودانية': 'السودان',
              'ليبي': 'ليبيا', 'ليبية': 'ليبيا',
              'تونسي': 'تونس', 'تونسية': 'تونس',
              'جزائري': 'الجزائر', 'جزائرية': 'الجزائر',
              'مغربي': 'المغرب', 'مغربية': 'المغرب',
              'هندي': 'الهند', 'هندية': 'الهند',
              'باكستاني': 'باكستان', 'باكستانية': 'باكستان',
              'بنغلاديشي': 'بنغلاديش', 'بنغلاديشية': 'بنغلاديش',
              'فلبيني': 'الفلبين', 'فلبينية': 'الفلبين',
              'اندونيسي': 'إندونيسيا', 'اندونيسية': 'إندونيسيا',
              'صيني': 'الصين', 'صينية': 'الصين',
              'بريطاني': 'بريطانيا', 'بريطانية': 'بريطانيا',
              'فرنسي': 'فرنسا', 'فرنسية': 'فرنسا',
              'الماني': 'ألمانيا', 'المانية': 'ألمانيا',
              'امريكي': 'أمريكا', 'امريكية': 'أمريكا',
              'كندي': 'كندا', 'كندية': 'كندا',
              'استرالي': 'أستراليا', 'استرالية': 'أستراليا',
              'روسي': 'روسيا', 'روسية': 'روسيا',
              'صومالي': 'الصومال', 'صومالية': 'الصومال',
              'اثيوبي': 'إثيوبيا', 'اثيوبية': 'إثيوبيا',
              'افغاني': 'أفغانستان', 'افغانية': 'أفغانستان',
              'سريلانكي': 'سريلانكا', 'سريلانكية': 'سريلانكا',
              'نيبالي': 'نيبال', 'نيبالية': 'نيبال',
            };
            // Normalize: strip diacritics, replace ى with ي, remove أإآ→ا
            const normalize = (s: string) => s.trim()
              .replace(/[\u064B-\u065F\u0670]/g, '') // remove tashkeel
              .replace(/ى/g, 'ي')                     // alef maqsura → ya
              .replace(/[أإآ]/g, 'ا');                 // normalize alef variants
            const raw = uaePass.nationality_ar.trim();
            const normalizedRaw = normalize(raw);
            // Try exact match first, then normalized match
            let mapped = natMap[raw];
            if (!mapped) {
              for (const [key, val] of Object.entries(natMap)) {
                if (normalize(key) === normalizedRaw) { mapped = val; break; }
              }
            }
            setNationality(mapped || raw);
            locked.add('nationality');
          }

          setSsoFields(locked);
          setPrefilledFromSSO(true);
        } catch (e) {
          console.log('[TalentRegistration] Failed to parse SSO user data:', e);
        }
      }
    }
  }, []);

  // ---- Load the talents list ----
  // Applicants look for a talent, not an organization, so the full published
  // list is shown with no filter. A ?dept= deep link still narrows it, which is
  // what the per-department invitation links rely on.
  useEffect(() => {
    setSelectedSpecs([]);
    (async () => {
      setSpecsLoading(true);
      try {
        const resp = await axios.get(`${API_BASE_URL}/talent-register/specializations/`, {
          params: deptId ? { dept: deptId } : undefined,
        });
        const items = resp.data?.results || resp.data || [];
        setSpecializations(items.map((s: any) => ({
          id: s.id, name: s.name, field: s.field, description: s.description,
          location: s.location || '', start_date: s.start_date || '', end_date: s.end_date || '',
          activity_schedules: s.activity_schedules || [],
          department_id: s.department_id ?? null,
        })));
      } catch (e) {
        console.log('[TalentRegistration] Failed to load specializations:', e);
      }
      setSpecsLoading(false);
    })();
  }, [deptId]);

  // ---- Load schools list ----
  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${API_BASE_URL}/talent-register/schools/`);
        setSchoolOptions(resp.data || []);
      } catch (e) {
        console.log('[TalentRegistration] Failed to load schools:', e);
      }
    })();
  }, []);

  // ---- Submit registration ----
  const handleSubmit = async () => {
    // Validation
    if (!name.trim()) {
      setErrorMsg('يرجى إدخال اسم الطالب');
      return;
    }
    if (!school.trim()) {
      setErrorMsg('يرجى إدخال اسم المدرسة');
      return;
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErrorMsg('يرجى إدخال بريد إلكتروني صحيح');
      return;
    }
    const eidDigits = eid.replace(/[^0-9]/g, '');
    if (eidDigits && eidDigits.length !== 15) {
      setErrorMsg(`رقم الهوية يجب أن يكون 15 رقم (الحالي: ${eidDigits.length})`);
      return;
    }
    if (phone.trim()) {
      if (phone.length !== 9 || !phone.startsWith('5')) {
        setErrorMsg('يرجى إدخال رقم هاتف صحيح (يبدأ بـ 5 ويتكون من 9 أرقام)');
        return;
      }
    }
    // Required: the backend derives the guardian requirement from it, and
    // refuses activation without it.
    if (!birthDate.trim()) {
      setErrorMsg('تاريخ الميلاد مطلوب');
      return;
    }
    {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(birthDate.trim())) {
        setErrorMsg('يرجى إدخال تاريخ الميلاد بالتنسيق الصحيح (YYYY-MM-DD)');
        return;
      }
      const d = new Date(birthDate.trim());
      const now = new Date();
      if (isNaN(d.getTime()) || d > now || d.getFullYear() < 1950) {
        setErrorMsg('يرجى إدخال تاريخ ميلاد صحيح');
        return;
      }
    }
    if (guardianRequired) {
      if (!guardianName.trim() || !guardianEid.trim() || !guardianPhone.trim()
          || !guardianEmail.trim() || !guardianRelation) {
        setErrorMsg('بيانات ولي الأمر مطلوبة للطلاب دون 15 سنة');
        return;
      }
      const guardianEidDigits = guardianEid.replace(/[^0-9]/g, '');
      if (guardianEidDigits.length !== 15) {
        setErrorMsg(`رقم هوية ولي الأمر يجب أن يكون 15 رقم (الحالي: ${guardianEidDigits.length})`);
        return;
      }
      if (guardianEidDigits === eid.replace(/[^0-9]/g, '')) {
        setErrorMsg('لا يمكن أن يكون الطالب ولي أمر نفسه');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guardianEmail.trim())) {
        setErrorMsg('يرجى إدخال بريد إلكتروني صحيح لولي الأمر');
        return;
      }
      if (!consentAccepted) {
        setErrorMsg('يلزم إقرار موافقة ولي الأمر');
        return;
      }
    }
    if (existingStudentMode) {
      const newSpecs = selectedSpecs.filter(id => !existingSpecIds.includes(id));
      if (newSpecs.length === 0) {
        setErrorMsg('يرجى اختيار موهبة جديدة واحدة على الأقل');
        return;
      }
    } else {
      if (selectedSpecs.length === 0) {
        setErrorMsg('يرجى اختيار موهبة واحدة على الأقل');
        return;
      }
    }
    // The applicant never picks an organization; the student's department is
    // the one owning the first talent they chose. Talents from other
    // departments still register fine — those are link rows, not the home
    // department.
    const resolvedDeptId =
      specializations.find((s) => s.id === selectedSpecs[0])?.department_id
      ?? (deptId ? Number(deptId) : null);

    if (!resolvedDeptId) {
      setErrorMsg('تعذر تحديد الجهة من الموهبة المختارة');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');
    try {
      if (existingStudentMode && existingStudentId) {
        // Add new specializations to existing student
        const newSpecIds = selectedSpecs.filter(id => !existingSpecIds.includes(id));
        await axios.post(`${API_BASE_URL}/talent-register/add-specializations/`, {
          student_id: existingStudentId,
          eid: eid.replace(/[^0-9]/g, ''),
          department_id: resolvedDeptId,
          specialization_ids: newSpecIds,
        });
        setSubmitted(true);
      } else {
        // Normal registration
        await axios.post(`${API_BASE_URL}/talent-register/`, {
          name: name.trim(),
          eid: eid.replace(/[^0-9]/g, '') || '',
          email: email.trim() || '',
          phone: phone.trim() ? `+971${phone.trim()}` : '',
          school: school.trim(),
          gender: gender || '',
          birth_date: birthDate,
          nationality: nationality || '',
          age_group: ageGroup || undefined,
          emirate: emirate || '',
          department_id: resolvedDeptId,
          specialization_ids: selectedSpecs,
          registration_method: prefilledFromSSO ? 'uaepass' : 'manual',
          ...(guardianRequired
            ? {
                guardian: {
                  name: guardianName.trim(),
                  eid: guardianEid.replace(/[^0-9]/g, ''),
                  email: guardianEmail.trim(),
                  phone: guardianPhone.trim(),
                  relation: guardianRelation,
                  consent_accepted: consentAccepted,
                },
              }
            : {}),
        });
        setSubmitted(true);
      }
    } catch (e: any) {
      console.log('[TalentRegistration] submit failed:', e);

      // Handle 409: existing student — switch to add specializations mode
      if (e.response?.status === 409 && e.response?.data?.existing_student_id) {
        const respData = e.response.data;
        setExistingStudentId(respData.existing_student_id);
        setExistingStudentName(respData.existing_student_name || '');
        setExistingSpecIds(respData.existing_specialization_ids || []);
        setExistingStudentMode(true);
        setSelectedSpecs(respData.existing_specialization_ids || []);
        setErrorMsg('');
        setSubmitting(false);
        return;
      }

      const detail = e.response?.data?.detail || e.response?.data?.error;
      if (detail) {
        setErrorMsg(detail);
      } else if (e.response?.data && typeof e.response.data === 'object') {
        const errs = Object.entries(e.response.data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join(' | ');
        setErrorMsg(errs || 'فشل في إرسال الطلب. يرجى المحاولة مرة أخرى.');
      } else {
        setErrorMsg('فشل في إرسال الطلب. يرجى المحاولة مرة أخرى.');
      }
    }
    setSubmitting(false);
  };

  // ---- Toggle spec selection ----
  const toggleSpec = useCallback((id: number) => {
    setSelectedSpecs((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  }, []);

  // ---- Close all dropdowns on outside press ----
  const closeAllDropdowns = () => {
    setShowGenderDropdown(false);
    setShowAgeGroupDropdown(false);
    setShowEmirateDropdown(false);
    setShowNationalityDropdown(false);
  };

  // ---- EID formatting ----
  const handleEidChange = (text: string) => {
    const raw = text.replace(/[^0-9]/g, '').slice(0, 15);
    let formatted = raw;
    if (raw.length > 3) formatted = raw.slice(0, 3) + '-' + raw.slice(3);
    if (raw.length > 7) formatted = raw.slice(0, 3) + '-' + raw.slice(3, 7) + '-' + raw.slice(7);
    if (raw.length > 14) formatted = raw.slice(0, 3) + '-' + raw.slice(3, 7) + '-' + raw.slice(7, 14) + '-' + raw.slice(14);
    setEid(formatted);
  };

  // Derived, never chosen: an under-15 birth date makes the guardian block
  // mandatory and the applicant cannot opt out of it.
  const guardianRequired = requiresGuardian(birthDate);

  // ---- Options ----
  const genderOptions = [
    { value: 'male', label: 'ذكر' },
    { value: 'female', label: 'أنثى' },
  ];
  const guardianRelationOptions = [
    { value: 'father', label: 'الأب' },
    { value: 'mother', label: 'الأم' },
    { value: 'legal_guardian', label: 'وصي قانوني' },
  ];
  const ageGroupOptions = [
    { value: 'foundation', label: 'المرحلة التأسيسية' },
    { value: 'intermediate', label: 'المرحلة المتوسطة' },
    { value: 'secondary', label: 'المرحلة الثانوية' },
  ];
  const emirateOptions = [
    { value: 'fujairah', label: 'الفجيرة' },
    { value: 'abu_dhabi', label: 'أبوظبي' },
    { value: 'dubai', label: 'دبي' },
    { value: 'sharjah', label: 'الشارقة' },
    { value: 'ajman', label: 'عجمان' },
    { value: 'umm_al_quwain', label: 'أم القيوين' },
    { value: 'ras_al_khaimah', label: 'رأس الخيمة' },
  ];
  const genderLabel = genderOptions.find((g) => g.value === gender)?.label || '';
  const ageGroupLabel = ageGroupOptions.find((a) => a.value === ageGroup)?.label || '';
  const emirateLabel = emirateOptions.find((e) => e.value === emirate)?.label || '';

  const nationalityOptions = [
    // الخليج العربي
    'الإمارات', 'السعودية', 'الكويت', 'البحرين', 'عمان', 'قطر',
    // الشرق الأوسط
    'الأردن', 'فلسطين', 'لبنان', 'سوريا', 'العراق', 'اليمن', 'مصر', 'إيران', 'تركيا',
    // شمال أفريقيا
    'السودان', 'ليبيا', 'تونس', 'الجزائر', 'المغرب', 'موريتانيا',
    // أفريقيا
    'الصومال', 'جيبوتي', 'جزر القمر', 'إريتريا', 'إثيوبيا', 'كينيا', 'تنزانيا', 'أوغندا',
    'نيجيريا', 'غانا', 'السنغال', 'مالي', 'تشاد', 'الكاميرون', 'جنوب أفريقيا',
    // جنوب آسيا
    'الهند', 'باكستان', 'بنغلاديش', 'سريلانكا', 'نيبال', 'أفغانستان', 'المالديف', 'بوتان',
    // جنوب شرق آسيا
    'الفلبين', 'إندونيسيا', 'ماليزيا', 'تايلاند', 'فيتنام', 'ميانمار', 'كمبوديا', 'سنغافورة',
    // شرق آسيا
    'الصين', 'اليابان', 'كوريا الجنوبية', 'كوريا الشمالية', 'تايوان', 'منغوليا',
    // آسيا الوسطى
    'أوزبكستان', 'كازاخستان', 'تركمانستان', 'قيرغيزستان', 'طاجيكستان',
    // أوروبا
    'بريطانيا', 'فرنسا', 'ألمانيا', 'إيطاليا', 'إسبانيا', 'البرتغال', 'هولندا', 'بلجيكا',
    'سويسرا', 'النمسا', 'السويد', 'النرويج', 'الدنمارك', 'فنلندا', 'بولندا', 'التشيك',
    'رومانيا', 'بلغاريا', 'المجر', 'اليونان', 'كرواتيا', 'صربيا', 'البوسنة والهرسك',
    'أوكرانيا', 'روسيا', 'بيلاروسيا', 'أيرلندا', 'أيسلندا',
    // أمريكا الشمالية
    'أمريكا', 'كندا', 'المكسيك',
    // أمريكا الجنوبية
    'البرازيل', 'الأرجنتين', 'كولومبيا', 'تشيلي', 'بيرو', 'فنزويلا', 'الإكوادور', 'بوليفيا', 'أوروغواي', 'باراغواي',
    // أوقيانوسيا
    'أستراليا', 'نيوزيلندا',
    // الكاريبي
    'كوبا', 'جامايكا', 'هايتي', 'جمهورية الدومينيكان',
  ];
  const filteredNationalities = nationalitySearch
    ? nationalityOptions.filter((n) => n.includes(nationalitySearch))
    : nationalityOptions;

  /* ---------------------------------------------------------------- */
  /*  Success state                                                    */
  /* ---------------------------------------------------------------- */
  if (submitted) {
    return (
      <View style={styles.fullCenter}>
        <Image
          source={require('../../../assets/images/Pattern BG.svg')}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', opacity: 0.5 } as any}
          resizeMode="cover"
        />
        <View style={[styles.successCard, { maxWidth: isMobile ? '90%' : 480 }]}>
          <CheckCircleIcon size={72} color={SUCCESS_GREEN} />
          <Text style={styles.successTitle}>{existingStudentMode ? 'تم إضافة المواهب بنجاح' : 'تم تسجيل الطلب بنجاح'}</Text>
          <Text style={styles.successMessage}>
            {existingStudentMode
              ? 'تم إضافة المواهب الجديدة إلى حسابك بنجاح.'
              : 'شكراً لتسجيلك في مبادرة مواهب. سيتم مراجعة طلبك والتواصل معك قريباً.'}
          </Text>
          <View style={styles.successDivider} />
          <Text style={styles.successNote}>
            {'يمكنك إغلاق هذه الصفحة الآن.'}
          </Text>
        </View>
      </View>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Main render                                                      */
  /* ---------------------------------------------------------------- */
  return (
    <View style={{ flex: 1, position: 'relative', backgroundColor: '#F5F5F5' }}>
      {/* Page-wide pattern background */}
      <Image
        source={require('../../../assets/images/Pattern BG.svg')}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', opacity: 0.4 } as any}
        resizeMode="repeat"
      />
    <ScrollView
      style={{ flex: 1, backgroundColor: 'transparent' }}
      contentContainerStyle={{ flexGrow: 1 }}
      showsVerticalScrollIndicator={false}
    >
      <TouchableOpacity activeOpacity={1} onPress={closeAllDropdowns} style={{ flex: 1 }}>
        {/* ============================================================ */}
        {/*  1. Header Banner                                            */}
        {/* ============================================================ */}
        {Platform.OS === 'web' ? (
          <div
            style={{
              background: 'linear-gradient(-8deg, #414042 28%, #A9211F 90%)',
              position: 'relative',
              overflow: 'hidden',
              padding: isMobile ? '32px 16px' : '40px 128px',
              display: 'flex',
              flexDirection: 'row-reverse',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 40,
              minHeight: isMobile ? 180 : 344,
            }}
          >
            {/* Right side: Logo + Title */}
            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', gap: 20, zIndex: 1 }}>
              <Image
                source={require('../../../assets/logo/logo-white.png')}
                style={{ width: 116, height: 120 } as any}
                resizeMode="contain"
              />
              <View style={{ alignItems: 'center', gap: 8 }}>
                <Text style={{ fontFamily: FONTS.medium, fontSize: isMobile ? 24 : 30, color: '#FFFFFF', textAlign: 'center' }}>
                  مبادرة مواهب
                </Text>
                <Text style={{ fontFamily: FONTS.regular, fontSize: isMobile ? 14 : 20, color: '#FFFFFF', textAlign: 'center' }}>
                  مبادرة سنوية مقامة برعاية مكتب سمو ولي العهد
                </Text>
              </View>
            </View>

            {/* Left side: Carousel card (desktop only) */}
            {!isMobile && specializations.length > 0 && (() => {
              const spec = specializations[carouselIndex % specializations.length];
              return (
                <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, zIndex: 1 }}>
                  {/* Carousel dots */}
                  <View style={{ alignItems: 'center', gap: 4 }}>
                    {specializations.map((_, i) => (
                      <TouchableOpacity key={i} onPress={() => setCarouselIndex(i)} activeOpacity={0.7}>
                        <View style={{
                          width: i === carouselIndex % specializations.length ? 8 : 6,
                          height: i === carouselIndex % specializations.length ? 8 : 6,
                          borderRadius: 4,
                          backgroundColor: i === carouselIndex % specializations.length ? '#FFFFFF' : 'rgba(255,255,255,0.4)',
                        }} />
                      </TouchableOpacity>
                    ))}
                  </View>

                  {/* Glass card */}
                  <div style={{
                    flex: 1,
                    backdropFilter: 'blur(2px)',
                    WebkitBackdropFilter: 'blur(2px)',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 8,
                    padding: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}>
                    {/* Spec name */}
                    <Text style={{ fontFamily: FONTS.medium, fontSize: 14, color: '#FFFFFF', textAlign: 'center' }}>
                      {spec.name}
                    </Text>
                    {/* Spec description */}
                    {spec.description ? (
                      <Text style={{ fontFamily: FONTS.regular, fontSize: 12, color: '#FFFFFF', textAlign: 'center' }} numberOfLines={2}>
                        {spec.description}
                      </Text>
                    ) : null}
                    {/* Academy row */}
                    <View style={{
                      flexDirection: 'row-reverse',
                      alignItems: 'center',
                      gap: 10,
                      borderWidth: 1,
                      borderColor: 'rgba(255,255,255,1)',
                      borderRadius: 4,
                      padding: 8,
                    }}>
                      <BuildingIcon size={20} color="#FFFFFF" />
                      <Text style={{ flex: 1, fontFamily: FONTS.regular, fontSize: 12, color: '#FFFFFF', textAlign: 'right' }}>
                        {spec.name}
                      </Text>
                    </View>
                    {/* Location row */}
                    <View style={{
                      flexDirection: 'row-reverse',
                      alignItems: 'center',
                      gap: 10,
                      borderWidth: 1,
                      borderColor: 'rgba(255,255,255,1)',
                      borderRadius: 4,
                      padding: 8,
                    }}>
                      <MapPinIcon size={20} color="#FFFFFF" />
                      <Text style={{ flex: 1, fontFamily: FONTS.regular, fontSize: 12, color: '#FFFFFF', textAlign: 'right' }}>
                        {spec.location || '--'}
                      </Text>
                    </View>
                    {/* Schedule row: dates + times + days all inside one bordered row */}
                    <View style={{
                      flexDirection: 'row-reverse',
                      alignItems: 'flex-start',
                      gap: 10,
                      borderWidth: 1,
                      borderColor: 'rgba(255,255,255,1)',
                      borderRadius: 4,
                      padding: 8,
                    }}>
                      <CalendarIcon size={16} color="#FFFFFF" />
                      <View style={{ flex: 1, gap: 4 }}>
                        {/* Date pills */}
                        {spec.start_date && spec.end_date ? (
                          <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 4 }}>
                            {(() => {
                              const fmtDate = (d: string) => {
                                const dt = new Date(d + 'T00:00:00');
                                return dt.toLocaleDateString('ar-AE', { day: 'numeric', month: 'long', year: 'numeric' });
                              };
                              return (
                                <>
                                  <View style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 2, paddingHorizontal: 8, paddingVertical: 2 }}>
                                    <Text style={{ fontFamily: FONTS.regular, fontSize: 12, color: '#FFFFFF' }}>{fmtDate(spec.start_date!)}</Text>
                                  </View>
                                  <View style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 2, paddingHorizontal: 8, paddingVertical: 2 }}>
                                    <Text style={{ fontFamily: FONTS.regular, fontSize: 12, color: '#FFFFFF' }}>{fmtDate(spec.end_date!)}</Text>
                                  </View>
                                </>
                              );
                            })()}
                          </View>
                        ) : (
                          <Text style={{ fontFamily: FONTS.regular, fontSize: 12, color: '#FFFFFF', textAlign: 'right' }}>--</Text>
                        )}
                        {/* Time + Day pills */}
                        {spec.activity_schedules && spec.activity_schedules.length > 0 && (() => {
                          const allTimes = spec.activity_schedules!.flatMap(a => a.times);
                          const uniqueTimes = [...new Set(allTimes)];
                          const allDays = spec.activity_schedules!.flatMap(a => a.days);
                          const uniqueDays = [...new Set(allDays)];
                          return (
                            <>
                              {uniqueTimes.length > 0 && (
                                <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 4 }}>
                                  {uniqueTimes.map((t: string) => (
                                    <View key={t} style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 2, paddingHorizontal: 8, paddingVertical: 2 }}>
                                      <Text style={{ fontFamily: FONTS.regular, fontSize: 12, color: '#FFFFFF' }}>{formatTimeRange12h(t)}</Text>
                                    </View>
                                  ))}
                                </View>
                              )}
                              {uniqueDays.length > 0 && (
                                <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 4 }}>
                                  {uniqueDays.map((day: string) => (
                                    <View key={day} style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 2, paddingHorizontal: 8, paddingVertical: 2 }}>
                                      <Text style={{ fontFamily: FONTS.regular, fontSize: 12, color: '#FFFFFF' }}>{day}</Text>
                                    </View>
                                  ))}
                                </View>
                              )}
                            </>
                          );
                        })()}
                      </View>
                    </View>
                  </div>
                </View>
              );
            })()}
          </div>
        ) : (
          <View style={styles.header}>
            <View style={{ alignItems: 'center', zIndex: 1, gap: 20 }}>
              <Image
                source={require('../../../assets/logo/logo-white.png')}
                style={{ width: 80, height: 80 } as any}
                resizeMode="contain"
              />
              <View style={{ alignItems: 'center', gap: 8 }}>
                <Text style={{ fontFamily: FONTS.medium, fontSize: 24, color: '#FFFFFF', textAlign: 'center' }}>
                  مبادرة مواهب
                </Text>
                <Text style={{ fontFamily: FONTS.regular, fontSize: 14, color: 'rgba(255,255,255,0.8)', textAlign: 'center' }}>
                  مبادرة سنوية مقامة برعاية مكتب سمو ولي العهد
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* ============================================================ */}
        {/*  2. Page Title                                               */}
        {/* ============================================================ */}
        <Text style={[styles.pageTitle, { maxWidth: 1100, alignSelf: 'center', width: '100%', paddingHorizontal: isMobile ? 16 : 0 }]}>
          {'استمارة التسجيل'}
        </Text>

        {/* Error Alert */}
        {errorMsg ? (
          <View style={[styles.errorAlert, { maxWidth: 1100, alignSelf: 'center', width: '100%', marginHorizontal: isMobile ? 16 : 0 }]}>
            <AlertIcon size={18} color={DANGER_RED} />
            <Text style={styles.errorAlertText}>{errorMsg}</Text>
            <TouchableOpacity onPress={() => setErrorMsg('')} style={styles.errorClose}>
              <Text style={styles.errorCloseText}>x</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {/* No "no departments" warning any more: the applicant never chooses an
            organization, so the only meaningful empty state is "no published
            talents", shown in the talents section below. */}

        {/* UAE Pass badge */}
        {prefilledFromSSO && (
          <View style={{ alignSelf: 'center', marginBottom: 16 }}>
            <View style={{ backgroundColor: '#F0FDFA', borderRadius: 20, paddingHorizontal: 16, paddingVertical: 6, borderWidth: 1, borderColor: '#00A79D' }}>
              <Text style={{ fontFamily: FONTS.regular, fontSize: 12, color: '#00A79D' }}>{'تم تعبئة البيانات من الهوية الرقمية'}</Text>
            </View>
          </View>
        )}

        {/* ============================================================ */}
        {/*  3. Personal Information Section                             */}
        {/* ============================================================ */}
        <View style={[styles.sectionCard, { maxWidth: 1100, alignSelf: 'center', width: '100%', marginHorizontal: isMobile ? 16 : 0, padding: 24, overflow: 'visible' as any, zIndex: 10 }]}>
          <Text style={styles.sectionTitle}>
            {'المعلومات الشخصية'}
          </Text>

          {/* Form grid: 2 columns on desktop */}
          <View style={[styles.formGrid, isMobile && { flexDirection: 'column' }]}>
            {/* Right column */}
            <View style={[styles.formGridCol, !isMobile && {}]}>
              {/* Row 1 Right: Full Name */}
              <FormField label={'الاسم الكامل'} required>
                <TextInput
                  style={[styles.input, ssoFields.has('name') && styles.disabledInput]}
                  value={name}
                  onChangeText={setName}
                  placeholder="الاسم الكامل"
                  placeholderTextColor={MUTED}
                  textAlign="right"
                  editable={!ssoFields.has('name')}
                />
              </FormField>

              {/* Row 2 Right: Phone */}
              <FormField label={'رقم الهاتف'} required>
                <View style={[styles.input, ssoFields.has('phone') && styles.disabledInput, { flexDirection: 'row', alignItems: 'center', paddingVertical: 0 }]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, borderRightWidth: 1, borderRightColor: BORDER, paddingRight: 10 }}>
                    <Text style={{ fontFamily: FONTS.medium, fontSize: 14, color: FOREGROUND }}>+971</Text>
                  </View>
                  <TextInput
                    ref={phoneRef}
                    style={{
                      flex: 1,
                      fontFamily: FONTS.regular,
                      fontSize: 14,
                      color: FOREGROUND,
                      paddingVertical: 12,
                      paddingHorizontal: 10,
                      textAlign: 'right',
                      ...(Platform.OS === 'web' ? { writingDirection: 'ltr' as any, outlineWidth: 0 } : {}),
                    } as any}
                    value={formattedPhone}
                    onChangeText={handlePhoneChange}
                    placeholder="5X XXX XXXX"
                    placeholderTextColor={MUTED}
                    keyboardType="phone-pad"
                    maxLength={12}
                    editable={!ssoFields.has('phone')}
                  />
                </View>
              </FormField>

              {/* Row 3 Right: Birth Date */}
              <FormField label={'تاريخ الميلاد'} required>
                {Platform.OS === 'web' ? (
                  <input
                    type="date"
                    value={birthDate}
                    max={new Date().toISOString().split('T')[0]}
                    min="1950-01-01"
                    onChange={(e) => setBirthDate(e.target.value)}
                    style={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #FFFFFF',
                      borderRadius: 8,
                      padding: '8px 12px',
                      fontFamily: FONTS.regular,
                      fontSize: 14,
                      color: FOREGROUND,
                      writingDirection: 'rtl',
                      textAlign: 'right',
                      width: '100%',
                      boxSizing: 'border-box' as any,
                      outline: 'none',
                    }}
                  />
                ) : (
                  <TextInput
                    style={[styles.input, styles.ltrInput]}
                    value={birthDate}
                    onChangeText={setBirthDate}
                    placeholder="YYYY-MM-DD"
                    placeholderTextColor={MUTED}
                  />
                )}
              </FormField>

              {/* Row 4 Right: Nationality (searchable dropdown) */}
              <FormField label={'الجنسية'} required>
                <View style={{ position: 'relative', zIndex: showNationalityDropdown ? 100 : 1 }}>
                  <TouchableOpacity
                    style={[styles.input, { flexDirection: 'row-reverse', alignItems: 'center' }, ssoFields.has('nationality') && styles.disabledInput]}
                    onPress={() => {
                      if (ssoFields.has('nationality')) return;
                      setShowNationalityDropdown(!showNationalityDropdown);
                      setShowGenderDropdown(false);
                      setShowAgeGroupDropdown(false);
                      setShowEmirateDropdown(false);
                      setNationalitySearch('');
                    }}
                    activeOpacity={ssoFields.has('nationality') ? 1 : 0.7}
                  >
                    <Text style={{ flex: 1, fontFamily: FONTS.regular, fontSize: 14, color: nationality ? FOREGROUND : MUTED, textAlign: 'right' }}>
                      {nationality || 'اختر الجنسية'}
                    </Text>
                    <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                      <Path d="M6 9l6 6 6-6" stroke={MUTED} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    </Svg>
                  </TouchableOpacity>
                  {showNationalityDropdown && (
                    <View style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#FFFFFF',
                      borderWidth: 1,
                      borderColor: BORDER,
                      borderTopWidth: 0,
                      borderBottomLeftRadius: 8,
                      borderBottomRightRadius: 8,
                      maxHeight: 250,
                      zIndex: 999,
                      ...(Platform.OS === 'web' ? { boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'visible' as any } : {}),
                    }}>
                      {/* Search input */}
                      <View style={{ padding: 8, borderBottomWidth: 1, borderBottomColor: BORDER }}>
                        {Platform.OS === 'web' ? (
                          <input
                            type="text"
                            value={nationalitySearch}
                            onChange={(e) => setNationalitySearch(e.target.value)}
                            placeholder="بحث..."
                            autoFocus
                            style={{
                              backgroundColor: '#F8FAFC',
                              border: '1px solid ' + BORDER,
                              borderRadius: 6,
                              padding: '6px 10px',
                              fontFamily: FONTS.regular,
                              fontSize: 13,
                              color: FOREGROUND,
                              writingDirection: 'rtl',
                              textAlign: 'right',
                              width: '100%',
                              boxSizing: 'border-box' as any,
                              outline: 'none',
                            }}
                          />
                        ) : (
                          <TextInput
                            value={nationalitySearch}
                            onChangeText={setNationalitySearch}
                            placeholder="بحث..."
                            placeholderTextColor={MUTED}
                            textAlign="right"
                            autoFocus
                            style={{
                              backgroundColor: '#F8FAFC',
                              borderWidth: 1,
                              borderColor: BORDER,
                              borderRadius: 6,
                              paddingHorizontal: 10,
                              paddingVertical: 6,
                              fontFamily: FONTS.regular,
                              fontSize: 13,
                              color: FOREGROUND,
                            }}
                          />
                        )}
                      </View>
                      {/* Options list */}
                      <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                        {filteredNationalities.map((nat) => (
                          <TouchableOpacity
                            key={nat}
                            style={{
                              paddingHorizontal: 14,
                              paddingVertical: 10,
                              backgroundColor: nationality === nat ? '#FEF2F2' : 'transparent',
                            }}
                            onPress={() => {
                              setNationality(nat);
                              setShowNationalityDropdown(false);
                              setNationalitySearch('');
                            }}
                          >
                            <Text style={{
                              fontFamily: nationality === nat ? FONTS.medium : FONTS.regular,
                              fontSize: 14,
                              color: nationality === nat ? BRAND_RED : FOREGROUND,
                              textAlign: 'right',
                            }}>
                              {nat}
                            </Text>
                          </TouchableOpacity>
                        ))}
                        {filteredNationalities.length === 0 && (
                          <Text style={{ fontFamily: FONTS.regular, fontSize: 13, color: MUTED, textAlign: 'center', padding: 16 }}>
                            لا توجد نتائج
                          </Text>
                        )}
                      </ScrollView>
                    </View>
                  )}
                </View>
              </FormField>
            </View>

            {/* Left column */}
            <View style={[styles.formGridCol, !isMobile && {}]}>
              {/* Row 1 Left: EID */}
              <FormField label={'رقم الهوية'}>
                {Platform.OS === 'web' ? (
                  <input
                    type="text"
                    value={eid}
                    onChange={(e) => handleEidChange(e.target.value)}
                    placeholder="784-XXXX-XXXXXXX-X"
                    disabled={ssoFields.has('eid')}
                    style={{
                      backgroundColor: ssoFields.has('eid') ? '#F1F5F9' : '#FFFFFF',
                      border: '1px solid #FFFFFF',
                      borderRadius: 8,
                      padding: '8px 12px',
                      fontFamily: FONTS.regular,
                      fontSize: 14,
                      color: FOREGROUND,
                      writingDirection: 'ltr',
                      textAlign: 'right',
                      width: '100%',
                      boxSizing: 'border-box' as any,
                      outline: 'none',
                      cursor: ssoFields.has('eid') ? 'not-allowed' : undefined,
                      opacity: ssoFields.has('eid') ? 0.7 : 1,
                    }}
                  />
                ) : (
                  <TextInput
                    ref={eidRef}
                    style={[styles.input, ssoFields.has('eid') && styles.disabledInput]}
                    value={eid}
                    editable={!ssoFields.has('eid')}
                    onChangeText={handleEidChange}
                    placeholder="784-XXXX-XXXXXXX-X"
                    placeholderTextColor={MUTED}
                    keyboardType="number-pad"
                    textAlign="right"
                  />
                )}
              </FormField>

              {/* Row 2 Left: Email — a minor's contact channel is their
                  guardian's, so the field is optional and labelled as such
                  once the birth date puts them under 15. */}
              <FormField
                label={guardianRequired ? 'البريد الالكتروني (اختياري)' : 'البريد الالكتروني'}
                required={!guardianRequired}
              >
                {Platform.OS === 'web' ? (
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@mail.com"
                    disabled={ssoFields.has('email')}
                    style={{
                      backgroundColor: ssoFields.has('email') ? '#F1F5F9' : '#FFFFFF',
                      border: '1px solid #FFFFFF',
                      borderRadius: 8,
                      padding: '8px 12px',
                      fontFamily: FONTS.regular,
                      fontSize: 14,
                      color: FOREGROUND,
                      writingDirection: 'rtl',
                      textAlign: 'right',
                      width: '100%',
                      boxSizing: 'border-box' as any,
                      outline: 'none',
                      cursor: ssoFields.has('email') ? 'not-allowed' : undefined,
                      opacity: ssoFields.has('email') ? 0.7 : 1,
                    }}
                  />
                ) : (
                  <TextInput
                    ref={emailRef}
                    style={[styles.input, ssoFields.has('email') && styles.disabledInput]}
                    value={email}
                    onChangeText={setEmail}
                    editable={!ssoFields.has('email')}
                    placeholder="example@mail.com"
                    placeholderTextColor={MUTED}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    textAlign="right"
                  />
                )}
              </FormField>

              {/* Row 3 Left: Emirate */}
              <FormField label={'الإمارة'} required>
                {Platform.OS === 'web' ? (
                  <select
                    value={emirate}
                    onChange={(e) => setEmirate(e.target.value)}
                    style={nativeSelectStyle(!!emirate)}
                  >
                    <option value="" disabled>{'اختر الإمارة'}</option>
                    {emirateOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <Dropdown
                    value={emirate}
                    label={emirateLabel}
                    options={emirateOptions}
                    open={showEmirateDropdown}
                    onToggle={() => { setShowEmirateDropdown(!showEmirateDropdown); setShowGenderDropdown(false); setShowAgeGroupDropdown(false); }}
                    onSelect={setEmirate}
                    placeholder={'اختر الإمارة'}
                  />
                )}
              </FormField>

              {/* Row 4 Left: Gender */}
              <FormField label={'الجنس'} required>
                {Platform.OS === 'web' ? (
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    disabled={ssoFields.has('gender')}
                    style={{
                      ...nativeSelectStyle(!!gender),
                      ...(ssoFields.has('gender') ? { backgroundColor: '#F1F5F9', cursor: 'not-allowed', opacity: 0.7 } : {}),
                    }}
                  >
                    <option value="" disabled>{'اختر الجنس'}</option>
                    {genderOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <Dropdown
                    value={gender}
                    label={genderLabel}
                    options={genderOptions}
                    open={showGenderDropdown}
                    onToggle={() => { setShowGenderDropdown(!showGenderDropdown); setShowAgeGroupDropdown(false); setShowEmirateDropdown(false); }}
                    onSelect={setGender}
                    placeholder={'اختر الجنس'}
                  />
                )}
              </FormField>
            </View>
          </View>
        </View>

        {/* ============================================================ */}
        {/*  3b. Guardian Section — students under 15 only                */}
        {/* ============================================================ */}
        {guardianRequired && (
          <View style={[styles.sectionCard, { maxWidth: 1100, alignSelf: 'center', width: '100%', marginTop: 24, marginHorizontal: isMobile ? 16 : 0, padding: 24 }]}>
            <Text style={styles.sectionTitle}>
              {'بيانات ولي الأمر'}
            </Text>
            <Text style={styles.guardianHint}>
              {'الطلاب دون 15 سنة يُسجَّلون عبر ولي الأمر، ويكون حساب الدخول باسمه.'}
            </Text>

            <View style={[styles.formGrid, isMobile && { flexDirection: 'column' }]}>
              {/* Right column */}
              <View style={styles.formGridCol}>
                <FormField label={'اسم ولي الأمر'} required>
                  <TextInput
                    style={styles.input}
                    value={guardianName}
                    onChangeText={setGuardianName}
                    placeholder={'الاسم الكامل لولي الأمر'}
                    placeholderTextColor={MUTED}
                  />
                </FormField>

                <FormField label={'رقم هاتف ولي الأمر'} required>
                  <TextInput
                    style={[styles.input, styles.ltrInput]}
                    value={guardianPhone}
                    onChangeText={(v) => setGuardianPhone(v.replace(/[^0-9+]/g, ''))}
                    placeholder="05X XXX XXXX"
                    placeholderTextColor={MUTED}
                    keyboardType="phone-pad"
                    maxLength={13}
                  />
                </FormField>
              </View>

              {/* Left column */}
              <View style={styles.formGridCol}>
                <FormField label={'رقم هوية ولي الأمر'} required>
                  <TextInput
                    style={[styles.input, styles.ltrInput]}
                    value={guardianEid}
                    onChangeText={(v) => setGuardianEid(v.replace(/[^0-9-]/g, ''))}
                    placeholder="784-XXXX-XXXXXXX-X"
                    placeholderTextColor={MUTED}
                    keyboardType="numeric"
                    maxLength={18}
                  />
                </FormField>

                <FormField label={'البريد الإلكتروني لولي الأمر'} required>
                  <TextInput
                    style={[styles.input, styles.ltrInput]}
                    value={guardianEmail}
                    onChangeText={setGuardianEmail}
                    placeholder={'يُستخدم لإنشاء حساب الدخول'}
                    placeholderTextColor={MUTED}
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                </FormField>
              </View>
            </View>

            <View style={[styles.formGrid, isMobile && { flexDirection: 'column' }]}>
              <View style={styles.formGridCol}>
                <FormField label={'صلة القرابة'} required>
                  {Platform.OS === 'web' ? (
                    <select
                      value={guardianRelation}
                      onChange={(e) => setGuardianRelation(e.target.value as typeof guardianRelation)}
                      style={nativeSelectStyle(true)}
                    >
                      {guardianRelationOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <Dropdown
                      value={guardianRelation}
                      label={guardianRelationOptions.find(o => o.value === guardianRelation)?.label || ''}
                      options={guardianRelationOptions}
                      open={showGuardianRelationDropdown}
                      onToggle={() => setShowGuardianRelationDropdown(!showGuardianRelationDropdown)}
                      onSelect={(v) => setGuardianRelation(v as typeof guardianRelation)}
                      placeholder={'اختر صلة القرابة'}
                    />
                  )}
                </FormField>
              </View>
              <View style={styles.formGridCol} />
            </View>

            <TouchableOpacity
              style={styles.consentRow}
              onPress={() => setConsentAccepted(!consentAccepted)}
              activeOpacity={0.8}
            >
              <View style={[styles.consentBox, consentAccepted && styles.consentBoxChecked]}>
                {consentAccepted && <Text style={styles.consentTick}>{'✓'}</Text>}
              </View>
              <Text style={styles.consentText}>
                {'أقر بأنني ولي أمر الطالب وأوافق على تسجيله في المبادرة وعلى معالجة بياناته.'}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* ============================================================ */}
        {/*  4. Academic Information Section                              */}
        {/* ============================================================ */}
        <View style={[styles.sectionCard, { maxWidth: 1100, alignSelf: 'center', width: '100%', marginTop: 24, marginHorizontal: isMobile ? 16 : 0, padding: 24 }]}>
          <Text style={styles.sectionTitle}>
            {'المعلومات الدراسية'}
          </Text>

          <View style={[styles.formGrid, isMobile && { flexDirection: 'column' }]}>
            {/* Right column: School */}
            <View style={[styles.formGridCol, !isMobile && {}]}>
              <FormField label={'المدرسة'} required>
                {Platform.OS === 'web' ? (
                  <select
                    value={school}
                    onChange={(e) => setSchool(e.target.value)}
                    style={nativeSelectStyle(!!school)}
                  >
                    <option value="" disabled>{'اختر المدرسة'}</option>
                    {schoolOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <TextInput
                    style={styles.input}
                    value={school}
                    onChangeText={setSchool}
                    placeholder={'اختر المدرسة'}
                    placeholderTextColor={MUTED}
                    textAlign="right"
                  />
                )}
              </FormField>
            </View>

            {/* Left column: Grade (ageGroup) */}
            <View style={[styles.formGridCol, !isMobile && {}]}>
              <FormField label={'الصف'} required>
                {Platform.OS === 'web' ? (
                  <select
                    value={ageGroup}
                    onChange={(e) => setAgeGroup(e.target.value)}
                    style={nativeSelectStyle(!!ageGroup)}
                  >
                    <option value="" disabled>{'اختر الصف'}</option>
                    {ageGroupOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <Dropdown
                    value={ageGroup}
                    label={ageGroupLabel}
                    options={ageGroupOptions}
                    open={showAgeGroupDropdown}
                    onToggle={() => { setShowAgeGroupDropdown(!showAgeGroupDropdown); setShowGenderDropdown(false); setShowEmirateDropdown(false); }}
                    onSelect={setAgeGroup}
                    placeholder={'اختر الصف'}
                  />
                )}
              </FormField>
            </View>
          </View>
        </View>

        {/* ============================================================ */}
        {/*  5. Available Talent Types Section                           */}
        {/* ============================================================ */}
        <View style={{ maxWidth: 1100, alignSelf: 'center', width: '100%', marginTop: 40, paddingHorizontal: isMobile ? 16 : 24 }}>
          <Text style={styles.talentSectionTitle}>
            {'أنواع المواهب المتاحة'}
          </Text>
          <Text style={styles.talentSectionSubtitle}>
            {'اختر موهبة واحدة أو أكثر تهمك. انقر على بطاقة الموهبة لاختيارها، واستخدم زر "المزيد" لعرض تفاصيل الموهبة الكاملة'}
          </Text>

          {/* No organization picker: applicants are looking for a talent, not
              an organization. The full published list is shown and the
              student's department is derived from the talent they choose. */}

          {specsLoading ? (
            <ActivityIndicator size="small" color={BRAND_RED} style={{ marginTop: 24 }} />
          ) : specializations.length === 0 ? (
            <Text style={styles.noSpecsText}>
              {'لا توجد مواهب متاحة حالياً'}
            </Text>
          ) : (
            <View style={[styles.specsGrid, { flexDirection: isDesktop ? 'row-reverse' : 'column' }]}>
              {existingStudentMode && (
                <View style={{
                  backgroundColor: '#EFF6FF', borderWidth: 1, borderColor: '#BFDBFE',
                  borderRadius: 12, padding: 16, marginBottom: 16, width: '100%',
                }}>
                  <Text style={{ fontFamily: FONTS.medium, fontSize: 14, color: '#1E40AF', textAlign: 'right' }}>
                    {`مرحباً ${existingStudentName}، أنت مسجل مسبقاً. يمكنك إضافة مواهب جديدة أدناه.`}
                  </Text>
                  <Text style={{ fontFamily: FONTS.regular, fontSize: 13, color: '#3B82F6', textAlign: 'right', marginTop: 4 }}>
                    {'المواهب المسجلة حالياً معلّمة باللون الرمادي ولا يمكن إلغاؤها.'}
                  </Text>
                </View>
              )}
              {specializations.map((spec) => {
                const isSelected = selectedSpecs.includes(spec.id);
                const isExistingSpec = existingStudentMode && existingSpecIds.includes(spec.id);
                const GOLD = '#A6813F';
                const metaBorderColor = isExistingSpec ? '#9CA3AF' : (isSelected ? FOREGROUND : BORDER);
                const metaTextColor = isExistingSpec ? '#9CA3AF' : (isSelected ? FOREGROUND : MUTED);
                return (
                  <TouchableOpacity
                    key={spec.id}
                    style={[
                      styles.specCard,
                      isSelected && {
                        borderColor: isExistingSpec ? '#9CA3AF' : GOLD,
                        borderWidth: 2,
                        backgroundColor: isExistingSpec ? '#F3F4F6' : '#FFFFFF',
                      },
                      isDesktop && { width: '48%' },
                      isExistingSpec && { opacity: 0.7 },
                    ]}
                    onPress={() => { if (!isExistingSpec) toggleSpec(spec.id); }}
                    activeOpacity={isExistingSpec ? 1 : 0.7}
                  >
                    {/* Header row: checkbox (left) + title (right) */}
                    <View style={styles.specCardHeader}>
                      <Text style={styles.specCardName}>
                        {spec.name}
                      </Text>
                      {isExistingSpec ? (
                        <View style={[styles.specCheckbox, { backgroundColor: '#9CA3AF', borderColor: '#9CA3AF' }]}>
                          <CheckIcon size={12} color="#FFFFFF" />
                        </View>
                      ) : isSelected ? (
                        <View style={[styles.specCheckbox, { backgroundColor: GOLD, borderColor: GOLD }]}>
                          <CheckIcon size={12} color="#FFFFFF" />
                        </View>
                      ) : (
                        <View style={styles.specCheckbox} />
                      )}
                    </View>
                    {isExistingSpec && (
                      <Text style={{ fontFamily: FONTS.medium, fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginBottom: 4 }}>
                        مسجل مسبقاً
                      </Text>
                    )}

                    {/* Description */}
                    {spec.description ? (
                      <Text style={styles.specCardDesc}>{spec.description}</Text>
                    ) : null}

                    {/* Metadata rows */}
                    <View style={styles.specMeta}>
                      {/* Location row */}
                      <View style={[styles.specMetaRow, { borderColor: metaBorderColor }]}>
                        <BuildingIcon size={16} color={metaTextColor} />
                        <Text style={[styles.specMetaText, { color: metaTextColor }]}>{spec.location || '--'}</Text>
                      </View>
                      {/* Address row */}
                      <View style={[styles.specMetaRow, { borderColor: metaBorderColor }]}>
                        <MapPinIcon size={16} color={metaTextColor} />
                        <Text style={[styles.specMetaText, { color: metaTextColor }]}>{spec.location ? `الفجيرة - ${spec.location}` : '--'}</Text>
                      </View>
                      {/* Schedule row: dates + times + days all inside one bordered row */}
                      <View style={[styles.specMetaRow, { borderColor: metaBorderColor, flexWrap: 'wrap', alignItems: 'flex-start' }]}>
                        <CalendarIcon size={16} color={metaTextColor} />
                        <View style={{ flex: 1, gap: 4 }}>
                          {/* Date pills */}
                          {spec.start_date && spec.end_date ? (
                            <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 4 }}>
                              {(() => {
                                const fmtDate = (d: string) => {
                                  const dt = new Date(d + 'T00:00:00');
                                  return dt.toLocaleDateString('ar-AE', { day: 'numeric', month: 'long', year: 'numeric' });
                                };
                                return (
                                  <>
                                    <View style={styles.specPill}><Text style={styles.specPillText}>{fmtDate(spec.start_date!)}</Text></View>
                                    <View style={styles.specPill}><Text style={styles.specPillText}>{fmtDate(spec.end_date!)}</Text></View>
                                  </>
                                );
                              })()}
                            </View>
                          ) : (
                            <Text style={[styles.specMetaText, { color: metaTextColor }]}>--</Text>
                          )}
                          {/* Time pills */}
                          {spec.activity_schedules && spec.activity_schedules.length > 0 && (() => {
                            const allTimes = spec.activity_schedules!.flatMap(a => a.times);
                            const uniqueTimes = [...new Set(allTimes)];
                            const allDays = spec.activity_schedules!.flatMap(a => a.days);
                            const uniqueDays = [...new Set(allDays)];
                            return (
                              <>
                                {uniqueTimes.length > 0 && (
                                  <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 4 }}>
                                    {uniqueTimes.map((t: string) => (
                                      <View key={t} style={styles.specPill}><Text style={styles.specPillText}>{formatTimeRange12h(t)}</Text></View>
                                    ))}
                                  </View>
                                )}
                                {uniqueDays.length > 0 && (
                                  <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 4 }}>
                                    {uniqueDays.map((day: string) => (
                                      <View key={day} style={styles.specPill}><Text style={styles.specPillText}>{day}</Text></View>
                                    ))}
                                  </View>
                                )}
                              </>
                            );
                          })()}
                        </View>
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          {/* ============================================================ */}
          {/*  6. Selected Specializations Summary Bar                     */}
          {/* ============================================================ */}
          {selectedSpecs.length > 0 && (
            <View style={styles.selectedBar}>
              <Text style={{ fontFamily: FONTS.medium, fontSize: 16, color: FOREGROUND, textAlign: 'right' }}>
                {`انواع المواهب المختارة (${selectedSpecs.length})`}
              </Text>
              {specializations
                .filter((s) => selectedSpecs.includes(s.id))
                .map((s) => (
                  <Text key={s.id} style={{ fontFamily: FONTS.regular, fontSize: 14, color: FOREGROUND, textAlign: 'right' }}>
                    {s.name}
                  </Text>
                ))}
            </View>
          )}
        </View>

        {/* ============================================================ */}
        {/*  7. Submit Button                                            */}
        {/* ============================================================ */}
        <View style={{ maxWidth: 1100, alignSelf: 'center', width: '100%', marginTop: 32, paddingHorizontal: isMobile ? 16 : 24 }}>
          <TouchableOpacity
            style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            activeOpacity={0.8}
            disabled={submitting}
          >
            {submitting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.submitButtonText}>{existingStudentMode ? 'إضافة المواهب الجديدة' : 'إرسال طلب التسجيل'}</Text>
            )}
          </TouchableOpacity>
        </View>

        {/* ============================================================ */}
        {/*  8. Footer                                                   */}
        {/* ============================================================ */}
        <View style={[styles.footer, isMobile && { paddingVertical: 20, paddingHorizontal: 16, marginTop: 32 }]}>
          <View style={[styles.footerContent, { flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 4 : 20, alignItems: isMobile ? 'center' : 'flex-start', justifyContent: 'flex-end' }]}>
            {/* Logo (desktop only) */}
            {!isMobile && (
              <Image
                source={require('../../../assets/logo/logo-Horizental-white.png')}
                style={{ width: 155, height: 64 } as any}
                resizeMode="contain"
              />
            )}
            {/* Text */}
            <View style={{ flex: isMobile ? undefined : 1, alignItems: isMobile ? 'center' : 'flex-end', gap: 4 }}>
              <Text style={[styles.footerTitle, isMobile && { fontSize: 16 }]}>
                برنامج دعم المواهب في الفجيرة
              </Text>
              <Text style={[styles.footerSubtitle, isMobile && { fontSize: 12, textAlign: 'center' }]}>
                تمكين المستقبل من خلال التعليم والابتكار
              </Text>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    </ScrollView>
    </View>
  );
}

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const styles = StyleSheet.create({
  /* -- Full-screen states -- */
  fullCenter: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    minHeight: '100%',
  } as any,

  /* -- Success -- */
  successCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    padding: 48,
    alignItems: 'center',
    gap: 16,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 24px rgba(0,0,0,0.08)' } : {}),
  },
  successTitle: {
    fontFamily: FONTS.medium,
    fontSize: 24,
    color: FOREGROUND,
    textAlign: 'center',
    marginTop: 8,
  },
  successMessage: {
    fontFamily: FONTS.regular,
    fontSize: 16,
    color: MUTED,
    textAlign: 'center',
    lineHeight: 26,
  },
  successDivider: {
    width: 60,
    height: 2,
    backgroundColor: '#E2E8F0',
    marginVertical: 8,
  },
  successNote: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: MUTED,
    textAlign: 'center',
  },

  /* -- Header (non-web fallback) -- */
  header: {
    backgroundColor: '#6B1D1B',
    paddingVertical: 40,
    paddingHorizontal: 24,
    minHeight: 200,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
  } as any,

  /* -- Page Title -- */
  pageTitle: {
    fontFamily: FONTS.medium,
    fontSize: 24,
    color: FOREGROUND,
    textAlign: 'right',
    marginTop: 40,
    marginBottom: 32,
  },

  /* -- Section Card -- */
  sectionCard: {
    backgroundColor: 'rgba(166, 129, 63, 0.2)',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: BORDER,
  },

  /* -- Section Title -- */
  sectionTitle: {
    fontFamily: FONTS.medium,
    fontSize: 18,
    color: FOREGROUND,
    textAlign: 'right',
    marginBottom: 24,
  },

  /* -- Guardian block (students under 15) -- */
  guardianHint: {
    fontFamily: FONTS.regular,
    fontSize: 13,
    color: MUTED,
    textAlign: 'right',
    marginTop: -16,
    marginBottom: 20,
  },
  consentRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
    marginTop: 8,
  },
  consentBox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  consentBoxChecked: {
    backgroundColor: '#A9211F',
    borderColor: '#A9211F',
  },
  consentTick: {
    color: '#FFFFFF',
    fontSize: 13,
    lineHeight: 16,
  },
  consentText: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: 13,
    color: FOREGROUND,
    textAlign: 'right',
  },

  /* -- Form grid -- */
  formGrid: {
    flexDirection: 'row-reverse',
    gap: 24,
  },
  formGridCol: {
    flex: 1,
  },

  /* -- Input -- */
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#FFFFFF',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minHeight: 36,
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: FOREGROUND,
    textAlign: 'right',
    ...(Platform.OS === 'web' ? { boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' } : {}),
  },

  ltrInput: {
    ...(Platform.OS === 'web' ? { writingDirection: 'ltr' as any, textAlign: 'right' as any } : {}),
  },

  disabledInput: {
    backgroundColor: '#F1F5F9',
    opacity: 0.7,
  },

  /* -- Talent Section -- */
  talentSectionTitle: {
    fontFamily: FONTS.medium,
    fontSize: 24,
    color: FOREGROUND,
    textAlign: 'center',
    marginBottom: 8,
  },
  talentSectionSubtitle: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: MUTED,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  noSpecsText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: MUTED,
    textAlign: 'center',
    paddingVertical: 20,
  },

  /* -- Specialization Cards Grid -- */
  specsGrid: {
    flexWrap: 'wrap',
    gap: 20,
    justifyContent: 'flex-start',
  },

  /* -- Specialization Card -- */
  specCard: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    padding: 20,
    flex: undefined,
  },
  specCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 10,
  },
  specCardName: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: FOREGROUND,
    flex: 1,
    textAlign: 'right',
  },
  specCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: '#CBD5E1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  specCardDesc: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: FOREGROUND,
    textAlign: 'right',
    marginTop: 8,
    lineHeight: 16,
  },
  specMeta: {
    marginTop: 8,
    gap: 8,
  },
  specMetaRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 4,
    padding: 8,
  },
  specMetaText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: FOREGROUND,
    flex: 1,
    textAlign: 'right',
  },
  specPill: {
    backgroundColor: '#E2E8F0',
    borderRadius: 2,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  specPillText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: FOREGROUND,
    textAlign: 'center',
  },
  specBadge: {
    backgroundColor: BORDER,
    borderRadius: 2,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  specBadgeText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: FOREGROUND,
    textAlign: 'center',
  },
  schedBadge: {
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  schedBadgeText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: FOREGROUND,
    textAlign: 'center',
  },

  /* -- Selected bar -- */
  selectedBar: {
    backgroundColor: '#DCFCE7',
    borderRadius: 4,
    borderRightWidth: 4,
    borderRightColor: '#22C55E',
    padding: 16,
    paddingHorizontal: 24,
    marginTop: 24,
    alignItems: 'flex-end',
    gap: 4,
  },

  /* -- Submit -- */
  submitButton: {
    backgroundColor: BRAND_RED,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  } as any,
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: '#F8FAFC',
  },

  /* -- Error / Warning alerts -- */
  errorAlert: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FEF2F2',
    borderWidth: 1,
    borderColor: '#FECACA',
    borderRadius: 12,
    padding: 14,
    gap: 10,
    marginBottom: 16,
  },
  errorAlertText: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#991B1B',
    textAlign: 'right',
  },
  errorClose: {
    padding: 4,
  },
  errorCloseText: {
    fontFamily: FONTS.regular,
    fontSize: 16,
    color: '#991B1B',
  },

  /* -- Footer -- */
  footer: {
    backgroundColor: FOREGROUND,
    marginTop: 60,
    paddingVertical: 40,
    paddingHorizontal: 120,
  },
  footerContent: {
    alignSelf: 'center',
    width: '100%',
    alignItems: 'center',
  } as any,
  footerTitle: {
    fontFamily: FONTS.medium,
    fontSize: 24,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  footerSubtitle: {
    fontFamily: FONTS.regular,
    fontSize: 16,
    color: '#FFFFFF',
    textAlign: 'right',
  },
});
